import React from "react";
import type { DryBulkNominationResponse } from "@services/dry-bulk-nomination/types";
import { useTranslation } from "react-i18next";
import { labelFor, useDryBulkReferenceOptions } from "../hooks/useDryBulkReferenceOptions";
import { formatUaeDateTime } from "../utils/formatUaeDate";
import DryBulkKeyValueGrid from "./ui/DryBulkKeyValueGrid";

const LANG_BASE = "vessel-nomination.dry-bulk.add";
const LANG_DETAILS = "vessel-nomination.dry-bulk.details";

interface Props {
  testId: string;
  nomination: DryBulkNominationResponse;
}

/** Read-only Nomination Details of the detail page (wireframe 09): label / value pairs, and the Remarks only when there are some. */
const DryBulkNominationSummary: React.FC<Props> = ({ testId, nomination }) => {
  const { t } = useTranslation();
  const { portOptions, purposeOfVisitOptions } = useDryBulkReferenceOptions();
  const remarks = nomination.remarks?.trim();

  const items = [
    {
      label: t(`${LANG_BASE}.fields.port`),
      value: nomination.portCode ? t(`${LANG_BASE}.ports.${nomination.portCode}`) : null,
    },
    {
      label: t(`${LANG_DETAILS}.fields.nomination-type`),
      value: t(`${LANG_DETAILS}.fields.nomination-type-value`),
    },
    {
      label: t(`${LANG_BASE}.fields.last-port`),
      value: labelFor(portOptions, nomination.lastPortCode ?? undefined),
    },
    {
      label: t(`${LANG_BASE}.fields.next-port`),
      value: labelFor(portOptions, nomination.nextPortCode ?? undefined),
    },
    {
      label: t(`${LANG_BASE}.fields.purpose-of-visit`),
      value: labelFor(purposeOfVisitOptions, nomination.purposeOfVisitCode ?? undefined),
    },
    { label: t(`${LANG_DETAILS}.fields.expected-berth`), value: nomination.expectedBerthCode },
    { label: t(`${LANG_BASE}.fields.berth-name`), value: nomination.expectedBerthName },
    { label: t(`${LANG_BASE}.fields.via-number`), value: nomination.viaNumber },
    { label: t(`${LANG_BASE}.fields.max-arrival-draft`), value: nomination.maxArrivalDraft },
    {
      label: t(`${LANG_DETAILS}.fields.initial-eta`),
      value: nomination.initialEta ? formatUaeDateTime(nomination.initialEta) : null,
    },
  ];

  return (
    <div className="flex flex-col gap-6" data-testid={testId}>
      <DryBulkKeyValueGrid
        testId={`${testId}-grid`}
        title={t(`${LANG_DETAILS}.nomination-details-title`)}
        items={items}
      />
      {remarks && (
        <DryBulkKeyValueGrid
          testId={`${testId}-remarks`}
          items={[{ label: t(`${LANG_DETAILS}.remarks`), value: remarks }]}
        />
      )}
    </div>
  );
};

export default DryBulkNominationSummary;
